import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  leadsApi,
  type Lead,
  type LeadPerformanceDrillMetric,
} from "@/api/leads";
import { hospitalAdminApi } from "@/api/hospitalAdmin";
import { Button } from "@/components/ui/button";
import { FilterMultiSelect } from "@/components/ui/filter-multi-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { isCrmViewOnlyUser, shouldMaskLeadPhone } from "@/utils/crmAccess";
import { maskPhoneDisplay } from "@/utils/stringUtils";
import { formatLeadStatusLabel, formatLeadSubStatusLabel } from "@/lib/crmLeadStyles";
import { LeadDetailModal } from "@/components/crm/LeadDetailModal";
import type { Staff } from "@/types/types";
import {
  EXITED_EMPLOYEES_BUCKET_ID,
  EXITED_EMPLOYEES_BUCKET_LABEL,
} from "@/constants/crmExitedEmployees";
import { reportHospitalStaffParams } from "@/utils/reportHospitalStaffParams";

export type LeadPerformanceMetrics = {
  leads: number;
  warm: number;
  dnps: number;
  lost: number;
  openPipeline: number;
  /** OPD bills by bill date when lead was created in the same period bucket. */
  opds: number;
  /** All OPD consultation bills by bill date (IST). */
  totalOpds: number;
  /** IPD discharges in period by discharge date (IST); lead created in same period bucket. */
  ipds: number;
  /** IPD discharges in period by discharge date (IST); any lead age. */
  totalIpds: number;
  l2o: number | null;
  opdsStillOpen: number;
  opdsLost: number;
  o2i: number | null;
  l2i: number | null;
  revenue: number;
  spent: number;
  roas: number | null;
};

export type LeadPerformanceRow = LeadPerformanceMetrics & {
  periodKey: string;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  campaigns?: (LeadPerformanceMetrics & { campaignName: string })[];
};

type LeadPerformanceBdSummaryRow = LeadPerformanceMetrics & {
  assigneeId: string;
  assigneeName: string;
  assigneeRole: "SALES_PERSON" | "SALES_MANAGER";
};

const DEFAULT_SOURCES = [
  "GOOGLE_PAID",
  "GMB",
  "META",
  "META_PAID",
  "WHATSAPP_INSTA_ADS",
  "WHATSAPP",
  "PRACTO",
  "HEXA",
  "REFERRAL",
  "WALK_IN",
  "OFFLINE",
  "OTHER",
  "EXOTEL",
  "ORGANIC_DIRECT",
  "ORGANIC_GOOGLE",
  "DIRECT_LANDING",
  "INSTAGRAM_ORGANIC",
];

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function fmtPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function fmtRoas(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(1)}x`;
}

function fmtMoney(v: number): string {
  return inr.format(v || 0);
}

const PERF_METRIC_LABELS: Record<LeadPerformanceDrillMetric, string> = {
  leads: "Leads",
  warm: "Warm",
  dnps: "DNPs",
  lost: "Lost",
  open_pipeline: "Open pipeline",
  opds: "OPDs",
  total_opds: "Total OPDs",
  ipds: "IPDs",
  total_ipds: "Total IPDs",
  opds_still_open: "OPDs open",
  opds_lost: "OPDs lost",
};

type PerfDrillContext = {
  periodStart: string;
  periodEnd: string;
  label: string;
  from: string;
  to: string;
  assignedToId?: string;
  campaignRow?: string;
};

type PerfDrillState = PerfDrillContext & { metric: LeadPerformanceDrillMetric };

function last30DateRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 29);
  return {
    from: start.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

function CountCell({
  value,
  metric,
  ctx,
  onDrill,
}: {
  value: number;
  metric: LeadPerformanceDrillMetric;
  ctx: PerfDrillContext;
  onDrill: (state: PerfDrillState) => void;
}) {
  if (value <= 0) {
    return <span className="tabular-nums text-muted-foreground">0</span>;
  }
  return (
    <button
      type="button"
      className="tabular-nums font-medium text-indigo-700 hover:underline"
      onClick={() => onDrill({ ...ctx, metric })}
    >
      {value}
    </button>
  );
}

function MetricsCells({
  m,
  drillCtx,
  onDrill,
}: {
  m: LeadPerformanceMetrics;
  drillCtx: PerfDrillContext;
  onDrill: (state: PerfDrillState) => void;
}) {
  return (
    <>
      <td className="p-2 text-center">
        <CountCell value={m.leads} metric="leads" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.warm} metric="warm" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.dnps} metric="dnps" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.lost} metric="lost" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.openPipeline} metric="open_pipeline" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.opds} metric="opds" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.totalOpds} metric="total_opds" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.ipds} metric="ipds" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.totalIpds} metric="total_ipds" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center tabular-nums text-muted-foreground italic">{fmtPct(m.l2o)}</td>
      <td className="p-2 text-center">
        <CountCell value={m.opdsStillOpen} metric="opds_still_open" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center">
        <CountCell value={m.opdsLost} metric="opds_lost" ctx={drillCtx} onDrill={onDrill} />
      </td>
      <td className="p-2 text-center tabular-nums text-muted-foreground italic">{fmtPct(m.o2i)}</td>
      <td className="p-2 text-center tabular-nums text-muted-foreground italic">{fmtPct(m.l2i)}</td>
      <td className="p-2 text-right tabular-nums whitespace-nowrap">{fmtMoney(m.revenue)}</td>
    </>
  );
}

type Props = {
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  hospitalId?: string;
};

export function LeadPerformanceTab({ from, to, onFromChange, onToChange, hospitalId }: Props) {
  const { user } = useAuth();
  const crmViewOnly = isCrmViewOnlyUser(user);
  const queryClient = useQueryClient();
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [campaignFilters, setCampaignFilters] = useState<string[]>([]);
  const [assignedToId, setAssignedToId] = useState<string>("all");
  const [groupMode, setGroupMode] = useState<"day" | "week" | "month" | "custom" | "campaign">("day");
  const [groupSize, setGroupSize] = useState("1");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingSpend, setEditingSpend] = useState<{
    periodStart: string;
    periodEnd: string;
    value: string;
  } | null>(null);
  const [drill, setDrill] = useState<PerfDrillState | null>(null);
  const [detailLead, setDetailLead] = useState<Lead | null>(null);
  const maskPhone = shouldMaskLeadPhone(user);

  const last30 = useMemo(() => last30DateRange(), []);

  const reportParams = useMemo(() => {
    const p: Record<string, string> = {
      from,
      to,
      groupMode,
      groupSize: groupMode === "custom" ? groupSize : "1",
      includeCampaigns: groupMode === "campaign" ? "false" : "true",
    };
    if (hospitalId) p.hospitalId = hospitalId;
    if (sourceFilters.length) p.source = sourceFilters.join(",");
    if (campaignFilters.length) p.campaignName = campaignFilters.join(",");
    if (assignedToId !== "all") p.assignedToId = assignedToId;
    return p;
  }, [from, to, groupMode, groupSize, hospitalId, sourceFilters, campaignFilters, assignedToId]);

  const { data: filterOptions } = useQuery({
    queryKey: ["lead-filter-options", hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(hospitalId ? { hospitalId } : undefined),
  });

  const { data: staffRows = [] } = useQuery<Staff[]>({
    queryKey: ["lead-performance-staff", hospitalId],
    queryFn: () =>
      hospitalAdminApi.getStaff(reportHospitalStaffParams(hospitalId, { includeInactive: true })),
  });

  const performanceQuery = useQuery({
    queryKey: ["lead-performance", reportParams],
    queryFn: async () => (await leadsApi.getLeadPerformanceReport(reportParams)).data.data,
  });

  const drillFilterParams = useMemo(
    () => ({
      hospitalId,
      source: sourceFilters.length ? sourceFilters.join(",") : undefined,
      campaignName: campaignFilters.length ? campaignFilters.join(",") : undefined,
    }),
    [hospitalId, sourceFilters, campaignFilters]
  );

  const { data: drillRows = [], isLoading: drillLoading } = useQuery({
    queryKey: ["lead-performance-drill", drill, drillFilterParams, assignedToId],
    queryFn: async () => {
      const res = await leadsApi.getLeadPerformanceDrill({
        ...drillFilterParams,
        from: drill!.from,
        to: drill!.to,
        periodStart: drill!.periodStart,
        periodEnd: drill!.periodEnd,
        metric: drill!.metric,
        assignedToId:
          drill!.assignedToId ??
          (assignedToId !== "all" ? assignedToId : undefined),
        campaignRow: drill!.campaignRow,
      });
      return res.data.data.items;
    },
    enabled: !!drill,
  });

  const drillNavLeads = useMemo(
    () =>
      drillRows.map(
        (row) =>
          ({
            id: row.leadId,
            patientName: row.patientName,
            phone: row.phone,
            source: row.source,
            status: row.status,
            leadSubStatus: row.leadSubStatus,
          }) as Lead
      ),
    [drillRows]
  );

  const spendMutation = useMutation({
    mutationFn: (payload: {
      spendDate: string;
      spendEnd?: string;
      amount: number;
      source?: string;
      campaignName?: string;
      hospitalId?: string;
    }) => leadsApi.upsertLeadAdSpend(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-performance"] });
      setEditingSpend(null);
      toast.success("Spend saved");
    },
    onError: () => toast.error("Failed to save spend"),
  });

  const sources = useMemo(() => {
    const fromApi = filterOptions?.sources ?? [];
    const merged = new Map<string, string>();
    for (const s of [...fromApi, ...DEFAULT_SOURCES]) {
      const t = String(s).trim();
      if (t) merged.set(t.toUpperCase(), t);
    }
    return [...merged.values()].sort((a, b) => a.localeCompare(b));
  }, [filterOptions?.sources]);

  const campaigns = filterOptions?.campaignNames ?? [];

  const sourceFilterOptions = useMemo(
    () => sources.map((s) => ({ value: s, label: s })),
    [sources]
  );

  const campaignFilterOptions = useMemo(
    () => campaigns.map((c) => ({ value: c, label: c })),
    [campaigns]
  );

  const spendSource = sourceFilters.length === 1 ? sourceFilters[0] : undefined;
  const spendCampaign = campaignFilters.length === 1 ? campaignFilters[0] : undefined;

  const bdOptions = useMemo(
    () =>
      staffRows
        .filter((staff) => {
          const role = String(staff.role || "").toUpperCase();
          if (role === "SALES_MANAGER") return staff.status === "ACTIVE";
          if (role === "SALES_PERSON") return staff.status === "ACTIVE" && !staff.exitedAt;
          return false;
        })
        .map((staff) => ({
          id: staff.id,
          name: staff.name,
          role: String(staff.role || "").toUpperCase() as "SALES_PERSON" | "SALES_MANAGER",
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [staffRows]
  );

  const rows: LeadPerformanceRow[] = performanceQuery.data?.rows ?? [];
  const totals: LeadPerformanceMetrics | undefined = performanceQuery.data?.totals;
  const bdSummaryRows: LeadPerformanceBdSummaryRow[] = performanceQuery.data?.bdSummaryLast30 ?? [];
  const allZero =
    !performanceQuery.isLoading &&
    rows.length > 0 &&
    rows.every((r) => r.leads === 0);

  const bdSummaryMerged = useMemo(() => {
    const byId = new Map(bdSummaryRows.map((row) => [row.assigneeId, row]));
    const emptyMetrics = (): LeadPerformanceMetrics => ({
      leads: 0,
      warm: 0,
      dnps: 0,
      lost: 0,
      openPipeline: 0,
      opds: 0,
      totalOpds: 0,
      ipds: 0,
      totalIpds: 0,
      l2o: null,
      opdsStillOpen: 0,
      opdsLost: 0,
      o2i: null,
      l2i: null,
      revenue: 0,
      spent: 0,
      roas: null,
    });

    const merged = bdOptions.map((bd) => {
      const existing = byId.get(bd.id);
      return (
        existing ?? {
          assigneeId: bd.id,
          assigneeName: bd.name,
          assigneeRole: bd.role,
          ...emptyMetrics(),
        }
      );
    });
    const exitedRow = byId.get(EXITED_EMPLOYEES_BUCKET_ID);
    if (exitedRow && !merged.some((row) => row.assigneeId === EXITED_EMPLOYEES_BUCKET_ID)) {
      merged.push(exitedRow);
    }
    return merged;
  }, [bdOptions, bdSummaryRows]);

  const setMonthPreset = (preset: "this-month" | "last-month" | "last-30") => {
    const now = new Date();
    if (preset === "last-30") {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      onFromChange(start.toISOString().slice(0, 10));
      onToChange(now.toISOString().slice(0, 10));
      return;
    }
    const y = now.getFullYear();
    const m = now.getMonth();
    if (preset === "this-month") {
      onFromChange(new Date(y, m, 1).toISOString().slice(0, 10));
      onToChange(now.toISOString().slice(0, 10));
    } else {
      onFromChange(new Date(y, m - 1, 1).toISOString().slice(0, 10));
      onToChange(new Date(y, m, 0).toISOString().slice(0, 10));
    }
  };

  const downloadCsv = async () => {
    const res = await leadsApi.downloadLeadPerformanceReport({
      ...reportParams,
      includeCampaigns: "false",
    });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-performance-${from}-to-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const toggleExpand = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const saveSpend = (
    periodStart: string,
    periodEnd: string,
    raw: string,
    rowCampaignName?: string
  ) => {
    const amount = parseFloat(raw.replace(/,/g, ""));
    if (Number.isNaN(amount) || amount < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const campaignFromRow =
      rowCampaignName && rowCampaignName !== "(No campaign)" ? rowCampaignName : undefined;
    spendMutation.mutate({
      spendDate: periodStart,
      spendEnd: periodEnd !== periodStart ? periodEnd : undefined,
      amount,
      source: spendSource,
      campaignName: campaignFromRow ?? spendCampaign,
      hospitalId,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Lead Performance</h2>
        <p className="text-sm text-muted-foreground">
          {groupMode === "campaign"
            ? "Lead funnel metrics grouped by campaign name (Leads = created in range; OPD/IPD columns = bill or discharge date)"
            : "Lead funnel metrics by date (Leads = created in bucket; OPD/IPD = bill or discharge date at any group unit). Click counts to open leads."}
        </p>
      </div>

      <div className="overflow-x-auto border rounded-md">
        <div className="px-3 py-2 border-b bg-slate-50">
          <p className="text-sm font-medium">Last 30 days by BD</p>
          <p className="text-xs text-muted-foreground">
            Lead columns use leads created in the last 30 days (source/campaign filters).{" "}
            <strong className="font-medium text-foreground/80">OPDs</strong> and{" "}
            <strong className="font-medium text-foreground/80">Total OPDs</strong> use OPD consultation
            bills (incl. ₹0) by bill date at any unit in your hospital group. OPDs = bill in window and
            lead created in the same 30 days; Total OPDs = all such bills in the window (any lead age).{" "}
            <strong className="font-medium text-foreground/80">IPDs</strong> and{" "}
            <strong className="font-medium text-foreground/80">Total IPDs</strong> use revenue IPD bills (discharge
            date, else bill date): first-touch CRM lead when phones match; IPDs = in window and lead created in the
            same 30 days; Total IPDs = all in the window (any lead age).
          </p>
        </div>
        <table className="min-w-max text-xs w-full">
          <thead className="bg-gray-50 text-muted-foreground">
            <tr>
              <th className="p-2 text-left whitespace-nowrap">BD / Owner</th>
              <th className="p-2 text-left whitespace-nowrap">Role</th>
              <th className="p-2 text-center">Leads</th>
              <th className="p-2 text-center">Warm</th>
              <th className="p-2 text-center">DNPs</th>
              <th className="p-2 text-center">Lost</th>
              <th className="p-2 text-center">Open Pipeline</th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="OPD consultation bills (any group unit) by bill date; lead created in same period bucket"
              >
                OPDs
              </th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="OPD consultation bills at any group unit (appointment-linked) by bill date in range"
              >
                Total OPDs
              </th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="IPD discharges at any group unit by discharge date; lead created in same 30-day window"
              >
                IPDs
              </th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="Discharged IPD admissions at any group unit by discharge date in last 30 days"
              >
                Total IPDs
              </th>
              <th className="p-2 text-center">L2O</th>
              <th className="p-2 text-center">OPDs Open</th>
              <th className="p-2 text-center">OPDs Lost</th>
              <th className="p-2 text-center">O2I</th>
              <th className="p-2 text-center">L2I</th>
              <th className="p-2 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {performanceQuery.isLoading ? (
              <tr>
                <td colSpan={17} className="p-4 text-center text-muted-foreground">
                  Loading BD summary…
                </td>
              </tr>
            ) : bdSummaryMerged.length === 0 ? (
              <tr>
                <td colSpan={17} className="p-4 text-center text-muted-foreground">
                  No active BDs found.
                </td>
              </tr>
            ) : (
              bdSummaryMerged.map((row, idx) => {
                const bdDrillCtx: PerfDrillContext = {
                  periodStart: last30.from,
                  periodEnd: last30.to,
                  label: `${row.assigneeName} · Last 30 days`,
                  from: last30.from,
                  to: last30.to,
                  assignedToId: row.assigneeId,
                };
                return (
                <tr
                  key={row.assigneeId}
                  className={`border-t ${idx % 2 === 1 ? "bg-gray-50/50" : ""} ${
                    row.assigneeId === EXITED_EMPLOYEES_BUCKET_ID ? "bg-amber-50/40" : ""
                  }`}
                >
                  <td className="p-2 font-medium whitespace-nowrap">{row.assigneeName}</td>
                  <td className="p-2 text-muted-foreground whitespace-nowrap">
                    {row.assigneeId === EXITED_EMPLOYEES_BUCKET_ID
                      ? "Exited team"
                      : row.assigneeRole === "SALES_MANAGER"
                        ? "Sales Manager"
                        : "Salesperson"}
                  </td>
                  <MetricsCells m={row} drillCtx={bdDrillCtx} onDrill={setDrill} />
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-3 border rounded-md bg-muted/30">
        <div className="space-y-1">
          <Label className="text-xs">Active BD</Label>
          <Select value={assignedToId} onValueChange={setAssignedToId}>
            <SelectTrigger className="w-[13rem] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All active BDs</SelectItem>
              {bdOptions.map((bd) => (
                <SelectItem key={bd.id} value={bd.id}>
                  {bd.name} {bd.role === "SALES_MANAGER" ? "(Manager)" : ""}
                </SelectItem>
              ))}
              <SelectItem value={EXITED_EMPLOYEES_BUCKET_ID}>{EXITED_EMPLOYEES_BUCKET_LABEL}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Source</Label>
          <FilterMultiSelect
            values={sourceFilters}
            onValuesChange={setSourceFilters}
            options={sourceFilterOptions}
            placeholder="Source"
            allLabel="All sources"
            triggerClassName="w-[11rem]"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Campaign</Label>
          <FilterMultiSelect
            values={campaignFilters}
            onValuesChange={setCampaignFilters}
            options={campaignFilterOptions}
            placeholder="Campaign"
            allLabel="All campaigns"
            triggerClassName="w-[11rem]"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Group by</Label>
          <Select value={groupMode} onValueChange={(v) => setGroupMode(v as typeof groupMode)}>
            <SelectTrigger className="w-[9rem] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
              <SelectItem value="custom">Custom days</SelectItem>
              <SelectItem value="campaign">Campaign name</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {groupMode === "custom" && (
          <div className="space-y-1">
            <Label className="text-xs">Days per group</Label>
            <Input
              type="number"
              min={1}
              max={90}
              className="w-20 h-9"
              value={groupSize}
              onChange={(e) => setGroupSize(e.target.value)}
            />
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-9 w-[10.5rem]" value={from} onChange={(e) => onFromChange(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[10.5rem]" value={to} onChange={(e) => onToChange(e.target.value)} />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Quick range</Label>
          <Select onValueChange={(v) => setMonthPreset(v as "this-month" | "last-month" | "last-30")}>
            <SelectTrigger className="w-[9rem] h-9">
              <SelectValue placeholder="Preset" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">This month</SelectItem>
              <SelectItem value="last-month">Last month</SelectItem>
              <SelectItem value="last-30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!crmViewOnly && (
          <Button variant="outline" size="sm" className="h-9" onClick={() => downloadCsv()}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border rounded-md max-h-[70vh] overflow-y-auto">
        <table className="min-w-max text-xs w-full">
          <thead className="bg-gray-50 text-muted-foreground sticky top-0 z-10">
            <tr>
              <th className="p-2 text-left w-8" />
              <th className="p-2 text-left whitespace-nowrap">
                {groupMode === "campaign" ? "Campaign" : "Period"}
              </th>
              <th className="p-2 text-center">Leads</th>
              <th className="p-2 text-center">Warm</th>
              <th className="p-2 text-center">DNPs</th>
              <th className="p-2 text-center">Lost</th>
              <th className="p-2 text-center">Open Pipeline</th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="OPD consultation bills (any group unit) by bill date; lead created in same period bucket"
              >
                OPDs
              </th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="OPD consultation bills at any group unit (appointment-linked) by bill date in period"
              >
                Total OPDs
              </th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="IPD discharges by discharge date; lead created in same period bucket"
              >
                IPDs
              </th>
              <th
                className="p-2 text-center whitespace-nowrap"
                title="Discharged IPD admissions at any group unit by discharge date in period"
              >
                Total IPDs
              </th>
              <th className="p-2 text-center">L2O</th>
              <th className="p-2 text-center">OPDs Open</th>
              <th className="p-2 text-center">OPDs Lost</th>
              <th className="p-2 text-center">O2I</th>
              <th className="p-2 text-center">L2I</th>
              <th className="p-2 text-right">Revenue</th>
              <th className="p-2 text-right">Spent</th>
              <th className="p-2 text-center">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {performanceQuery.isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-t animate-pulse">
                  <td colSpan={19} className="p-3">
                    <div className="h-4 bg-muted rounded w-full" />
                  </td>
                </tr>
              ))
            ) : allZero ? (
              <tr>
                <td colSpan={19} className="p-8 text-center text-muted-foreground">
                  No leads found for this period and filters.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const expanded = expandedRows.has(row.periodKey);
                const hasCampaigns =
                  groupMode !== "campaign" && (row.campaigns?.length ?? 0) > 0;
                const spendEditing =
                  editingSpend?.periodStart === row.periodStart &&
                  editingSpend?.periodEnd === row.periodEnd;
                const rowDrillCtx: PerfDrillContext = {
                  periodStart: row.periodStart,
                  periodEnd: row.periodEnd,
                  label: row.periodLabel,
                  from,
                  to,
                  campaignRow:
                    groupMode === "campaign" && row.periodLabel !== "(No campaign)"
                      ? row.periodLabel
                      : undefined,
                };

                return (
                  <Fragment key={row.periodKey}>
                    <tr className={`border-t ${idx % 2 === 1 ? "bg-gray-50/50" : ""}`}>
                      <td className="p-2">
                        {hasCampaigns ? (
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => toggleExpand(row.periodKey)}
                          >
                            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        ) : null}
                      </td>
                      <td className="p-2 font-medium whitespace-nowrap">{row.periodLabel}</td>
                      <MetricsCells m={row} drillCtx={rowDrillCtx} onDrill={setDrill} />
                      <td className="p-2 text-right">
                        {spendEditing ? (
                          <Input
                            className="h-7 w-24 text-right text-xs ml-auto"
                            autoFocus
                            value={editingSpend.value}
                            onChange={(e) =>
                              setEditingSpend((s) => (s ? { ...s, value: e.target.value } : s))
                            }
                            onBlur={() => {
                              if (editingSpend) {
                                saveSpend(
                                  row.periodStart,
                                  row.periodEnd,
                                  editingSpend.value,
                                  groupMode === "campaign" ? row.periodLabel : undefined
                                );
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && editingSpend) {
                                saveSpend(
                                  row.periodStart,
                                  row.periodEnd,
                                  editingSpend.value,
                                  groupMode === "campaign" ? row.periodLabel : undefined
                                );
                              }
                              if (e.key === "Escape") setEditingSpend(null);
                            }}
                          />
                        ) : crmViewOnly ? (
                          <span className="tabular-nums text-right w-full block">{fmtMoney(row.spent)}</span>
                        ) : (
                          <button
                            type="button"
                            className="tabular-nums hover:underline text-right w-full"
                            onClick={() =>
                              setEditingSpend({
                                periodStart: row.periodStart,
                                periodEnd: row.periodEnd,
                                value: String(row.spent || ""),
                              })
                            }
                          >
                            {fmtMoney(row.spent)}
                          </button>
                        )}
                      </td>
                      <td className="p-2 text-center tabular-nums text-muted-foreground italic">
                        {fmtRoas(row.roas)}
                      </td>
                    </tr>
                    {expanded &&
                      row.campaigns?.map((c) => {
                        const campDrillCtx: PerfDrillContext = {
                          periodStart: row.periodStart,
                          periodEnd: row.periodEnd,
                          label: `${row.periodLabel} · ${c.campaignName}`,
                          from,
                          to,
                          campaignRow:
                            c.campaignName !== "(No campaign)" ? c.campaignName : undefined,
                        };
                        return (
                        <tr key={`${row.periodKey}-${c.campaignName}`} className="border-t bg-blue-50/30">
                          <td className="p-2" />
                          <td className="p-2 pl-6 text-muted-foreground whitespace-nowrap">{c.campaignName}</td>
                          <MetricsCells m={c} drillCtx={campDrillCtx} onDrill={setDrill} />
                          <td className="p-2 text-right text-muted-foreground">—</td>
                          <td className="p-2 text-center text-muted-foreground">—</td>
                        </tr>
                        );
                      })}
                  </Fragment>
                );
              })
            )}
            {totals && !performanceQuery.isLoading && !allZero && (
              <tr className="border-t-2 border-gray-300 bg-gray-100 font-semibold sticky bottom-0">
                <td className="p-2" />
                <td className="p-2">Totals</td>
                <MetricsCells
                  m={totals}
                  drillCtx={{
                    periodStart: from,
                    periodEnd: to,
                    label: "Totals",
                    from,
                    to,
                  }}
                  onDrill={setDrill}
                />
                <td className="p-2 text-right tabular-nums">{fmtMoney(totals.spent)}</td>
                <td className="p-2 text-center tabular-nums text-muted-foreground italic">{fmtRoas(totals.roas)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!drill}
        onOpenChange={(open) => {
          if (!open) {
            setDrill(null);
            setDetailLead(null);
          }
        }}
      >
        <DialogContent className="flex max-h-[min(88vh,720px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
            <DialogTitle>
              {drill
                ? `${PERF_METRIC_LABELS[drill.metric]} · ${drill.label}`
                : "Lead performance drill-down"}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {drillLoading ? (
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </p>
            ) : drillRows.length === 0 ? (
              <p className="text-muted-foreground text-sm">No leads for this cell.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2">Patient</th>
                    <th className="py-2 pr-2">Phone</th>
                    <th className="py-2 pr-2">Source</th>
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2 pr-2">Campaign</th>
                    <th className="py-2 pr-2">Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {drillRows.map((row) => (
                    <tr
                      key={row.leadId}
                      className="cursor-pointer border-b hover:bg-muted/40"
                      onClick={() =>
                        setDetailLead({
                          id: row.leadId,
                          patientName: row.patientName,
                          phone: row.phone,
                          source: row.source,
                          status: row.status,
                          leadSubStatus: row.leadSubStatus,
                        } as Lead)
                      }
                    >
                      <td className="py-2 pr-2 font-medium">{row.patientName}</td>
                      <td className="py-2 pr-2">
                        {maskPhone ? maskPhoneDisplay(row.phone) : row.phone}
                      </td>
                      <td className="py-2 pr-2">{row.source}</td>
                      <td className="py-2 pr-2">
                        {formatLeadStatusLabel(row.status as Lead["status"])}
                        {row.leadSubStatus
                          ? ` · ${formatLeadSubStatusLabel(row.leadSubStatus as Lead["leadSubStatus"])}`
                          : ""}
                      </td>
                      <td className="py-2 pr-2">{row.campaignName ?? "—"}</td>
                      <td className="py-2 pr-2">{row.assigneeName ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <LeadDetailModal
        open={!!detailLead}
        onOpenChange={(open) => {
          if (!open) setDetailLead(null);
        }}
        anchorLead={detailLead}
        navLeads={drillNavLeads}
      />
    </div>
  );
}
